import * as sessionReplay from '@amplitude/session-replay-browser';
import { AnalyticsBrowser } from '@segment/analytics-next';
import * as Sentry from '@sentry/browser';

import store from '../store/store';
import { getActiveMapFilters } from './mapFilters';

const NETLIFY_PREVIEW_PR_REGEX = /([0-9a-z]{20,30}--|deploy-preview)/;

// Re-export for backward compatibility
export { getActiveMapFilters };

// Prevent companies to be tracked

const isCompanyBlockedFromTracking = (): boolean => {
  try {
    const blockedIds = (
      process.env.REACT_APP_BLOCKED_TRACKING_COMPANY_IDS || ''
    )
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (blockedIds.length === 0) return false;

    const state: any = store.getState();
    const companyId = state?.user?.info?.companyId;
    return companyId ? blockedIds.includes(String(companyId)) : false;
  } catch {
    return false;
  }
};

/**
 * Check if tracking should be disabled for current host
 *
 * By default, tracking is disabled for localhost and Netlify preview PRs.
 * To enable tracking on localhost for testing, set REACT_APP_ENABLE_LOCALHOST_TRACKING=true
 * in your .env file.
 *
 * @returns {boolean} true if tracking should be disabled, false otherwise
 */

const isHostDisabledForTracking = (): boolean => {
  const { host } = window.location;
  const enableLocalhostTracking =
    process.env.REACT_APP_ENABLE_LOCALHOST_TRACKING === 'true';

  // Always disable tracking for Netlify preview PRs
  if (host.match(NETLIFY_PREVIEW_PR_REGEX)) {
    return true;
  }

  // Disable tracking for localhost unless explicitly enabled
  return host.includes('localhost') && !enableLocalhostTracking;
};

/**
 * Get screen and device information for analytics
 */
export const getScreenInfo = () => {
  // Get screen dimensions (physical screen size)
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  // Get viewport/window dimensions (visible area)
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // Get device pixel ratio for retina/high-DPI displays
  const devicePixelRatio = window.devicePixelRatio || 1;

  // Determine device type based on screen width
  // Using standard breakpoints: mobile (<768px), tablet (768px-1024px), desktop (>1024px)
  let deviceType = 'desktop';
  if (windowWidth < 768) {
    deviceType = 'mobile';
  } else if (windowWidth >= 768 && windowWidth <= 1024) {
    deviceType = 'tablet';
  }

  // Determine screen orientation
  const orientation = windowWidth > windowHeight ? 'landscape' : 'portrait';

  return {
    screenWidth,
    screenHeight,
    windowWidth,
    windowHeight,
    devicePixelRatio,
    deviceType,
    orientation,
    // Additional useful info
    colorDepth: window.screen.colorDepth,
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
  };
};

const segmentWriteKey = process.env.REACT_APP_SEGMENT_WRITE_KEY || '';

export const analytics = segmentWriteKey
  ? AnalyticsBrowser.load({
      writeKey: segmentWriteKey,
    })
  : ({
      track: () => Promise.resolve(),
      identify: () => Promise.resolve(),
      page: () => Promise.resolve(),
      group: () => Promise.resolve(),
      alias: () => Promise.resolve(),
      screen: () => Promise.resolve(),
      reset: () => Promise.resolve(),
      ready: () => Promise.resolve(),
      user: () => ({
        anonymousId: () => null,
      }),
      setAnonymousId: () => {},
      addSourceMiddleware: () => {},
    } as any);

const analyticsSentryLimitDuration = process.env
  .REACT_APP_ANALYTICS_SENTRY_THROTTLE_DURATION
  ? parseInt(process.env.REACT_APP_ANALYTICS_SENTRY_THROTTLE_DURATION, 10) *
    60 *
    1000
  : 10 * 60 * 1000;
const lastSentryErrorTime: { [key: string]: number } = {};
const reportAnalyticsErrorToSentry = (
  error: Error,
  errorKey: string,
  extraData: any,
) => {
  const now = Date.now();
  const lastErrorTime = lastSentryErrorTime[errorKey] || 0;

  // Check if enough time has passed since last error of this type

  if (now - lastErrorTime >= analyticsSentryLimitDuration) {
    lastSentryErrorTime[errorKey] = now;

    Sentry.withScope((scope) => {
      scope.setTag('component', 'Analytics');
      scope.setLevel('error');
      scope.setExtra('throttled', true);
      scope.setExtra('throttleDuration', analyticsSentryLimitDuration);

      // Add all extra data
      Object.keys(extraData).forEach((key) => {
        scope.setExtra(key, extraData[key]);
      });

      scope.setExtra('error message', error?.message);
      Sentry.captureException(error);
    });
  }
};

/**
 * Get cookie value by name
 */
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

/**
 * Set cookie value
 */
const setCookie = (name: string, value: string, days = 365): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

/**
 * Get stored session ID from cookie
 */
const getStoredSessionId = (): number => {
  const sessionId = getCookie('analytics_session_id');
  return sessionId ? parseInt(sessionId, 10) : 0;
};

/**
 * Map from URL module segment to display name as shown in UI sidebar
 */
const MODULE_DISPLAY_NAMES: Record<string, string> = {
  assess: 'Assess',
  optimize: 'Optimize',
  discover: 'Discover',
  'my-places': 'My Places',
  'chaos-ai': 'CHAOS AI',
  'ai-reports': 'AI Reports',
};

/**
 * Map from URL feature segment to display name as shown in UI top bar
 */
const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  'population-structure': 'People',
  services: 'Services',
  households: 'Households',
  'tenant-search': 'Companies',
  footfall: 'Footfall',
  'area-development': 'Plot Search',
  'property-market': 'Properties',
  reams: 'Rent Navigator',
  'rent-navigator': 'Rent Navigator',
  'residential-market': 'Prices',
  'retail-catchment-analysis': 'Catchment Analysis',
  'retail-market-analysis': 'Scaling Simulation',
};

/**
 * Get current module based on route path
 * Returns display name as shown in UI: 'Assess', 'Optimize', 'Discover', 'CHAOS AI', 'AI Reports', 'Account', or undefined
 */
export const getCurrentModule = (): string | undefined => {
  if (typeof window === 'undefined' || !window.location) return undefined;

  const { pathname } = window.location;
  const firstSegment = pathname.split('/')?.[1];

  if (firstSegment && MODULE_DISPLAY_NAMES[firstSegment]) {
    return MODULE_DISPLAY_NAMES[firstSegment];
  }

  // Account routes (authentication and settings)
  if (
    pathname === '/' ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/sign-in') ||
    pathname.startsWith('/create-password') ||
    pathname.startsWith('/password-recovery') ||
    pathname.startsWith('/settings')
  ) {
    return 'Account';
  }

  return undefined;
};

/**
 * Get current feature based on route path
 * Returns display name as shown in UI top bar: 'People', 'Prices', 'Rent Navigator', etc.
 */
const getCurrentFeature = (): string | undefined => {
  if (typeof window === 'undefined' || !window.location) return undefined;

  const { pathname } = window.location;
  const secondSegment = pathname.split('/')?.[2];

  if (secondSegment && FEATURE_DISPLAY_NAMES[secondSegment]) {
    return FEATURE_DISPLAY_NAMES[secondSegment];
  }

  return undefined;
};

const getModuleName = (module?: string): string | undefined =>
  module ? MODULE_DISPLAY_NAMES[module] || module : getCurrentModule();

/**
 * Get trial/signup analytics properties from Redux store.
 * Injected into every Segment track event via middleware.
 */
export const getTrialAnalyticsProperties = (): Record<string, any> => {
  try {
    const state: any = store.getState();
    const globalTrialFeatures: string[] =
      state?.user?.globalTrialFeatures || [];
    const isSelfSignup: boolean = state?.user?.info?.isSelfSignUp || false;

    const licensedFeatures: string[] = state?.user?.globalFeatures || [];
    const trialFeatures = globalTrialFeatures.filter(
      (f) => !licensedFeatures.includes(f),
    );

    const hasTrial = trialFeatures.length > 0;
    const hasLicensed = licensedFeatures.length > 0;

    let userAccessType: string | undefined;
    if (hasTrial && hasLicensed) userAccessType = 'licensed_with_trial';
    else if (hasLicensed) userAccessType = 'licensed_only';
    else if (hasTrial) userAccessType = 'trial_only';

    let trialSource: string;
    if (!hasTrial) trialSource = 'not_trial';
    else if (isSelfSignup) trialSource = 'self_serve';
    else trialSource = 'assisted';

    return {
      ...(userAccessType && { userAccessType }),
      trialSource,
    };
  } catch {
    return {};
  }
};

// Only add middleware if Segment is properly initialized
if (
  segmentWriteKey &&
  analytics &&
  typeof analytics.addSourceMiddleware === 'function'
) {
  // Middleware to filter out disabled hosts (localhost and Netlify preview PRs)
  // Can be enabled for localhost by setting REACT_APP_ENABLE_LOCALHOST_TRACKING=true
  analytics.addSourceMiddleware(({ payload, next }: any) => {
    if (!isHostDisabledForTracking() && !isCompanyBlockedFromTracking()) {
      next(payload);
    }
  });

  // Middleware to sync Session Replay session ID with Segment
  // Following Amplitude documentation: https://amplitude.com/docs/session-replay/session-replay-integration-with-segment
  analytics.addSourceMiddleware(({ payload, next }: any) => {
    const storedSessionId = getStoredSessionId();

    setTimeout(() => {
      try {
        // Get session ID from Segment Amplitude integration
        const integrations = payload.obj?.integrations || {};
        const actionsAmplitude = integrations['Actions Amplitude'];
        const amplitudeClassic = integrations.Amplitude;
        const amplitudeIntegration = actionsAmplitude || amplitudeClassic;
        const nextSessionId = amplitudeIntegration?.session_id || 0;

        // Update Session Replay if session ID changed
        if (nextSessionId > storedSessionId) {
          setCookie('analytics_session_id', nextSessionId.toString());
          sessionReplay.setSessionId(nextSessionId);
        }
      } catch (_error) {
        // Silently fail
      }
    }, 0);

    next(payload);
  });

  // Middleware to add Session Replay properties to all track events
  // Required by Amplitude documentation
  analytics.addSourceMiddleware(({ payload, next }: any) => {
    try {
      if (payload.type() === 'track') {
        const sessionReplayProperties =
          sessionReplay.getSessionReplayProperties();
        if (
          sessionReplayProperties &&
          Object.keys(sessionReplayProperties).length > 0
        ) {
          // eslint-disable-next-line no-param-reassign
          Object.assign(payload.obj.properties, sessionReplayProperties);
        }
      }
    } catch (_error) {
      // Error handled silently
    }

    next(payload);
  });

  // Middleware to add trial user properties to all track and page events
  analytics.addSourceMiddleware(({ payload, next }: any) => {
    try {
      const type = payload.type();
      if (type === 'track' || type === 'page') {
        const trialProperties = getTrialAnalyticsProperties();
        if (Object.keys(trialProperties).length > 0) {
          Object.assign(payload.obj.properties, trialProperties);
        }
      }
    } catch (_error) {
      // Error handled silently
    }

    next(payload);
  });

  // Middleware to add feature property to all track and page events
  analytics.addSourceMiddleware(({ payload, next }: any) => {
    try {
      const type = payload.type();
      if (type === 'track' || type === 'page') {
        const feature = getCurrentFeature();
        if (feature) {
          Object.assign(payload.obj.properties, { feature });
        }
      }
    } catch (_error) {
      // Error handled silently
    }

    next(payload);
  });

  // Middleware to fall back dashboardTitle to feature name when not set explicitly
  analytics.addSourceMiddleware(({ payload, next }: any) => {
    try {
      const type = payload.type();
      if (type === 'track' || type === 'page') {
        const props = payload.obj.properties;
        if (!props.dashboardTitle) {
          const feature = getCurrentFeature();
          if (feature) {
            Object.assign(props, { dashboardTitle: feature });
          }
        }
      }
    } catch (_error) {
      // Error handled silently
    }

    next(payload);
  });
}

/**
 * Track page view with URL
 */
export const trackPageView = (url: string, module?: string) => {
  if (url === '/') return;

  try {
    const currentModule = getModuleName(module);

    const feature = getCurrentFeature();
    const nameParts = [currentModule, feature].filter(Boolean);
    const pageName = nameParts.length > 0 ? nameParts.join(' / ') : 'Unknown';

    analytics.page(pageName, {
      url,
      ...(currentModule && { module: currentModule }),
      ...(feature && { feature }),
    });
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackPageView', {
      function: 'trackPageView',
      url,
      module,
    });
  }
};

/**
 * Identify user with screen information
 */
export const identifyUser = (userId: string, traits?: any) => {
  try {
    // Get screen information to help diagnose UI issues
    const screenInfo = getScreenInfo();

    // Combine user traits with screen information
    const enhancedTraits = {
      ...traits,
      // Screen and device information
      screen: {
        width: screenInfo.screenWidth,
        height: screenInfo.screenHeight,
      },
      viewport: {
        width: screenInfo.windowWidth,
        height: screenInfo.windowHeight,
      },
      devicePixelRatio: screenInfo.devicePixelRatio,
      deviceType: screenInfo.deviceType,
      orientation: screenInfo.orientation,
      colorDepth: screenInfo.colorDepth,
      touchSupport: screenInfo.touchSupport,
    };

    analytics.identify(userId, enhancedTraits);
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'identifyUser', {
      function: 'identifyUser',
      userId,
      traits: JSON.stringify(traits),
    });
  }
};

/**
 * Track user login
 * Account events always use 'Account' as module to separate account events from module-specific events
 */
export const trackLogin = (
  method: string,
  userId?: string,
  module?: string,
) => {
  try {
    // Always use 'Account' as module for account events
    analytics.track('user_login', {
      method,
      userId,
      module: 'Account',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackLogin', {
      function: 'trackLogin',
      method,
      userId,
      module,
    });
  }
};

/**
 * Track user signup
 * Account events always use 'Account' as module to separate account events from module-specific events
 */
export const trackSignup = (
  method: string,
  trialFeature?: string,
  userId?: string,
  module?: string,
) => {
  try {
    // Always use 'Account' as module for account events
    analytics.track('user_signup', {
      method,
      trialFeature,
      userId,
      module: 'Account',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackSignup', {
      function: 'trackSignup',
      method,
      trialFeature,
      userId,
      module,
    });
  }
};

/**
 * Track user logout
 * Account events always use 'Account' as module to separate account events from module-specific events
 */
export const trackLogout = (userId?: string, module?: string) => {
  try {
    // Always use 'Account' as module for account events
    analytics.track('user_logout', {
      userId,
      module: 'Account',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackLogout', {
      function: 'trackLogout',
      userId,
      module,
    });
  }
};

/**
 * Track forgot password request
 * Account events always use 'Account' as module to separate account events from module-specific events
 */
export const trackForgotPassword = (email: string, module?: string) => {
  try {
    // Always use 'Account' as module for account events
    analytics.track('forgot_password', {
      email,
      module: 'Account',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackForgotPassword', {
      function: 'trackForgotPassword',
      email,
      module,
    });
  }
};

/**
 * Track contact us click
 */
export const trackContactUsClick = (page?: string, module?: string) => {
  try {
    const currentModule = getModuleName(module);
    analytics.track('contact_us_click', {
      page: page || 'unknown',
      ...(currentModule && { module: currentModule }),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackContactUsClick', {
      function: 'trackContactUsClick',
      page,
      module,
    });
  }
};

/**
 * Track in-app trial activation by an existing (logged-in) user
 */
export const trackTrialActivated = (trialFeature: string) => {
  try {
    analytics.track('trial_activated', {
      trialFeature,
      module: 'Account',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackTrialActivated', {
      function: 'trackTrialActivated',
      trialFeature,
    });
  }
};

/**
 * Track "Contact sales" submit from the trial expired popup
 */
export const trackTrialExpiredContactSales = (source: string) => {
  try {
    analytics.track('trial_expired_contact_sales', {
      source,
      module: 'Account',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(
      error as Error,
      'trackTrialExpiredContactSales',
      {
        function: 'trackTrialExpiredContactSales',
        source,
      },
    );
  }
};

/**
 * Track dashboard filter selection
 */
export const trackDashboardFilter = (
  filterName: string,
  previousFilter?: string,
  dashboardTitle?: string,
  module?: string,
) => {
  try {
    const currentModule = getModuleName(module);
    analytics.track('dashboard_filter_selected', {
      filterName,
      previousFilter,
      ...(dashboardTitle && { dashboardTitle }),
      ...(currentModule && { module: currentModule }),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackDashboardFilter', {
      function: 'trackDashboardFilter',
      filterName,
      previousFilter,
      module,
    });
  }
};

/**
 * Track Insights data download (CSV exports from Property Market modals, etc.)
 */
export const trackInsightsDownload = (
  downloadType: string,
  dashboardTitle?: string,
  downloadData?: any,
  module?: string,
) => {
  try {
    const currentModule = getModuleName(module);
    analytics.track('insights_download', {
      downloadType, // 'property_owners_csv', 'transactions_csv', etc.
      ...(dashboardTitle && { dashboardTitle }),
      ...(currentModule && { module: currentModule }),
      ...downloadData,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackInsightsDownload', {
      function: 'trackInsightsDownload',
      downloadType,
      dashboardTitle,
      module,
      downloadData: JSON.stringify(downloadData),
    });
  }
};

const serializeBoundingBox = (mapBoundingBox: any) => {
  try {
    if (!mapBoundingBox?.southWest || !mapBoundingBox?.northEast) return null;

    return {
      southWest: {
        lat: mapBoundingBox.southWest.lat,
        lng: mapBoundingBox.southWest.lng,
      },
      northEast: {
        lat: mapBoundingBox.northEast.lat,
        lng: mapBoundingBox.northEast.lng,
      },
    };
  } catch (_error) {
    return null;
  }
};

/**
 * Track CHAOS AI prompt submission
 */
export const trackChaosAiPromptSubmitted = ({
  conversationId,
  messageId,
  mapBoundingBox,
}: {
  conversationId: string;
  messageId?: string;
  mapBoundingBox?: any;
}) => {
  try {
    const serializedBoundingBox = serializeBoundingBox(mapBoundingBox);

    analytics.track('chaos_ai_prompt_submitted', {
      conversationId,
      ...(messageId && { messageId }),
      ...(serializedBoundingBox && { mapBoundingBox: serializedBoundingBox }),
      module: 'CHAOS AI',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(
      error as Error,
      'trackChaosAiPromptSubmitted',
      {
        function: 'trackChaosAiPromptSubmitted',
        conversationId,
      },
    );
  }
};

/**
 * Helper function to prepare analytics data for different region types
 */
const prepareMapRegionAnalyticsData = (regionType: string, regionData: any) => {
  const baseData = {
    regionType,
    timestamp: new Date().toISOString(),
    centerPoint: regionData?.centerPoint,
    selectedYear: regionData?.selectedYear,
    country: regionData?.country,
    municipality: regionData?.municipality,
  };

  // Add specific data based on region type
  switch (regionType) {
    case 'essentials_hexagon':
      return {
        ...baseData,
        selectedDataset: regionData?.selectedEssentialsDataset,
        selectedSegment: regionData?.selectedEssentialsSegment,
        areaValue: regionData?.areaValue,
        demographicData: regionData?.demographicData
          ? {
              ageDependencyRatio: regionData.demographicData.ageDependencyRatio,
              labourForce: regionData.demographicData.labourForce,
              meanIncome: regionData.demographicData.meanIncome,
              unemployedRate: regionData.demographicData.unemployedRate,
              workplaces: regionData.demographicData.workplaces,
              inhabitantsTotal: regionData.demographicData.inhabitantsTotal,
              males: regionData.demographicData.males,
              females: regionData.demographicData.females,
              dataSource: regionData.demographicData.dataSource,
            }
          : undefined,
      };

    case 'plot_property':
      return {
        ...baseData,
        propertyData: regionData?.propertyData
          ? {
              propertyId: regionData.propertyData.propertyId,
              totalArea: regionData.propertyData.totalArea,
              estimatedTotalArea: regionData.propertyData.estimatedTotalArea,
              totalAvailableBuildingRights:
                regionData.propertyData.totalAvailableBuildingRights,
              usedBuildingRights: regionData.propertyData.usedBuildingRights,
              availableBuildingRights:
                regionData.propertyData.availableBuildingRights,
              landEfficiency: regionData.propertyData.landEfficiency,
              buildPotential: regionData.propertyData.buildPotential,
              zoningTypes: regionData.propertyData.zoningTypes,
              zoningSubTypes: regionData.propertyData.zoningSubTypes,
              zoningDate: regionData.propertyData.zoningDate,
              intendedUseMml: regionData.propertyData.intendedUseMml,
            }
          : undefined,
      };

    case 'building':
      return {
        ...baseData,
        buildingData: regionData?.buildingData
          ? {
              buildingId: regionData.buildingData.buildingId,
              dateOfCompletion: regionData.buildingData.dateOfCompletion,
              floorArea: regionData.buildingData.floorArea,
              numberOfFloors: regionData.buildingData.numberOfFloors,
              numberOfApartments: regionData.buildingData.numberOfApartments,
              useHierarchy: regionData.buildingData.useHierarchy,
              buildingAge: regionData.buildingData.buildingAge,
            }
          : undefined,
      };

    case 'property_market_property':
      return {
        ...baseData,
        propertyData: regionData?.propertyData
          ? {
              propertyId: regionData.propertyData.propertyId,
              totalArea: regionData.propertyData.totalArea,
              use: regionData.propertyData.use,
              numberOfBuildings: regionData.propertyData.numberOfBuildings,
              totalAvailableBuildingRights:
                regionData.propertyData.totalAvailableBuildingRights,
              landEfficiency: regionData.propertyData.landEfficiency,
              usedBuildingRights: regionData.propertyData.usedBuildingRights,
              availableBuildingRights:
                regionData.propertyData.availableBuildingRights,
              zoningType: regionData.propertyData.zoningType,
              hasTransactions: regionData.propertyData.hasTransactions,
              hasOwners: regionData.propertyData.hasOwners,
              dataSource: regionData.propertyData.dataSource,
              sourcedAt: regionData.propertyData.sourcedAt,
            }
          : undefined,
      };

    case 'property_market_building':
      return {
        ...baseData,
        buildingData: regionData?.buildingData
          ? {
              buildingId: regionData.buildingData.buildingId,
              address: regionData.buildingData.address,
              useHierarchy: regionData.buildingData.useHierarchy,
              floorArea: regionData.buildingData.floorArea,
              numberOfFloors: regionData.buildingData.numberOfFloors,
              numberOfApartments: regionData.buildingData.numberOfApartments,
              heatingMethod: regionData.buildingData.heatingMethod,
              heatingFuel: regionData.buildingData.heatingFuel,
              dateOfCompletion: regionData.buildingData.dateOfCompletion,
              buildingAge: regionData.buildingData.buildingAge,
              hasTenants: regionData.buildingData.hasTenants,
              dataSource: regionData.buildingData.dataSource,
              sourcedAt: regionData.buildingData.sourcedAt,
            }
          : undefined,
      };

    case 'residential_market_hexagon':
      return {
        ...baseData,
        hexagonData: regionData?.hexagonData
          ? {
              hexagonId: regionData.hexagonData.hexagonId,
              areaValue: regionData.hexagonData.areaValue,
              selectedYear: regionData.hexagonData.selectedYear,
              marketType: regionData.hexagonData.marketType, // rentLevel, askingPrice
              dataSource: regionData.hexagonData.dataSource,
              // Additional data from properties.data[selectedYear]
              yearData: regionData.hexagonData.yearData
                ? {
                    medianPerSquareMeter:
                      regionData.hexagonData.yearData.medianPerSquareMeter,
                    numberOfAds: regionData.hexagonData.yearData.numberOfAds,
                    averageLivingArea:
                      regionData.hexagonData.yearData.averageLivingArea,
                    ownerOccupiedDwellings:
                      regionData.hexagonData.yearData.ownerOccupiedDwellings,
                    rentedDwellings:
                      regionData.hexagonData.yearData.rentedDwellings,
                    totalDwellings:
                      regionData.hexagonData.yearData.totalDwellings,
                    oneAndTwoRoomDwellings:
                      regionData.hexagonData.yearData.oneAndTwoRoomDwellings,
                    threeAndFourRoomDwellings:
                      regionData.hexagonData.yearData.threeAndFourRoomDwellings,
                    fiveAndMoreRoomDwellings:
                      regionData.hexagonData.yearData.fiveAndMoreRoomDwellings,
                    householdsTotal:
                      regionData.hexagonData.yearData.householdsTotal,
                    oneTwoPersonHouseholds:
                      regionData.hexagonData.yearData.oneTwoPersonHouseholds,
                    threeFourPersonHouseholds:
                      regionData.hexagonData.yearData.threeFourPersonHouseholds,
                    fivePlusPersonHouseholds:
                      regionData.hexagonData.yearData.fivePlusPersonHouseholds,
                    medianRent: regionData.hexagonData.yearData.medianRent,
                    dataSource: regionData.hexagonData.yearData.dataSource,
                  }
                : undefined,
            }
          : undefined,
      };

    case 'service_mix_point':
      return {
        ...baseData,
        serviceData: regionData?.serviceData
          ? {
              pointId: regionData.serviceData.pointId,
              serviceType: regionData.serviceData.serviceType,
              name: regionData.serviceData.name,
              address: regionData.serviceData.address,
              category: regionData.serviceData.category,
              subcategory: regionData.serviceData.subcategory,
              dataSource: regionData.serviceData.dataSource,
            }
          : undefined,
      };

    case 'service_mix_hexagon':
      return {
        ...baseData,
        hexagonData: regionData?.hexagonData
          ? {
              hexagonId: regionData.hexagonData.hexagonId,
              areaValue: regionData.hexagonData.areaValue,
              selectedYear: regionData.hexagonData.selectedYear,
              serviceType: regionData.hexagonData.serviceType, // diversity, density, reachability, competitiveness
              dataSource: regionData.hexagonData.dataSource,
            }
          : undefined,
      };

    case 'tenant_search_point':
      return {
        ...baseData,
        tenantData: regionData?.tenantData
          ? {
              pointId: regionData.tenantData.pointId,
              businessId: regionData.tenantData.businessId,
              companyName: regionData.tenantData.companyName,
              address: regionData.tenantData.address,
              mainCategory: regionData.tenantData.mainCategory,
              subCategory: regionData.tenantData.subCategory,
              phoneNumber: regionData.tenantData.phoneNumber,
              numberOfEmployees: regionData.tenantData.numberOfEmployees,
              turnoverCategory: regionData.tenantData.turnoverCategory,
              dataSource: regionData.tenantData.dataSource,
              sourcedAt: regionData.tenantData.sourcedAt,
            }
          : undefined,
      };

    case 'households_hexagon':
      return {
        ...baseData,
        hexagonData: regionData?.hexagonData
          ? {
              hexagonId: regionData.hexagonData.hexagonId,
              areaValue: regionData.hexagonData.areaValue,
              selectedYear: regionData.hexagonData.selectedYear,
              householdType: regionData.hexagonData.householdType,
              dataSource: regionData.hexagonData.dataSource,
            }
          : undefined,
      };

    case 'footfall_hexagon':
      return {
        ...baseData,
        hexagonData: regionData?.hexagonData
          ? {
              hexagonId: regionData.hexagonData.hexagonId,
              areaValue: regionData.hexagonData.areaValue,
              selectedYear: regionData.hexagonData.selectedYear,
              footfallType: regionData.hexagonData.footfallType,
              dataSource: regionData.hexagonData.dataSource,
            }
          : undefined,
      };

    case 'advanced_household_types':
      return {
        ...baseData,
        householdData: regionData?.householdData
          ? {
              areaId: regionData.householdData.areaId,
              selectedYear: regionData.householdData.selectedYear,
              selectedTab: regionData.householdData.selectedTab,
              yearData: regionData.householdData.yearData,
              dataSource: regionData.householdData.dataSource,
            }
          : undefined,
      };

    case 'reams_property':
      return {
        ...baseData,
        propertyData: regionData?.propertyData
          ? {
              propertyName: regionData.propertyData.propertyName,
              dataset: regionData.propertyData.dataset,
              areaCode: regionData.propertyData.areaCode,
              countryCode: regionData.propertyData.countryCode,
              municipality: regionData.propertyData.municipality,
              estimatedRent: regionData.propertyData.estimatedRent,
              estimatedRentDifferencePercentage:
                regionData.propertyData.estimatedRentDifferencePercentage,
              coordinates: regionData.propertyData.coordinates,
            }
          : undefined,
        clickLocation: regionData?.clickLocation
          ? {
              lat: regionData.clickLocation.lat,
              lng: regionData.clickLocation.lng,
            }
          : undefined,
      };

    case 'retail_place':
      return {
        ...baseData,
        placeData: regionData?.placeData
          ? {
              placeId: regionData.placeData.placeId,
              placeName: regionData.placeData.placeName,
              placeAddress: regionData.placeData.placeAddress,
              placeCategory: regionData.placeData.placeCategory,
              placeAttractiveness: regionData.placeData.placeAttractiveness,
              placeBusinessLine: regionData.placeData.placeBusinessLine,
              placeAttractivenessAdjustmentPercentage:
                regionData.placeData.placeAttractivenessAdjustmentPercentage,
              placeStoreSize: regionData.placeData.placeStoreSize,
              placeCoordinates: regionData.placeData.placeCoordinates,
              retailFeature: regionData.placeData.retailFeature,
            }
          : undefined,
        clickLocation: regionData?.clickLocation
          ? {
              lat: regionData.clickLocation.lat,
              lng: regionData.clickLocation.lng,
            }
          : undefined,
      };

    default:
      return {
        ...baseData,
        customData: regionData?.customData,
      };
  }
};

/**
 * Track map filter change event with comprehensive filter context
 *
 * @param {string} viewOnMap - Dashboard/indicator name (e.g., 'Area development', 'Property market', 'Residential demand-supply')
 * @param {string} filterType - Type of filter that was changed (e.g., 'plotLandEfficiency', 'selectedFillColorFilter', 'propertyOwnerModalSearch')
 * @param {any} filterValue - New filter value (can be a single value, array, or object depending on filter type)
 * @param {any} [previousFilterValue] - Previous filter value before the change
 * @param {any} [legendState] - Legend state object for extracting active filters. Should contain dashboard-specific state (e.g., { propertyMarketState: {...} } or { areaDevLegend: {...} })
 * @param {string} [module] - Module name (e.g., 'Assess', 'Optimize', 'Discover'). Auto-detected from URL if not provided.
 * @returns {void}
 *
 * @example
 * trackMapFilterChange(
 *   'Property market',
 *   'propertyOwnerModalSearch',
 *   { searchTerm: 'search term', columnFilter: 'propertyId' },
 *   { searchTerm: 'previous term', columnFilter: 'propertyId' },
 *   { propertyMarketState: {...} }
 * )
 */
export const trackMapFilterChange = (
  viewOnMap: string,
  filterType: string,
  filterValue: any,
  previousFilterValue?: any,
  legendState?: any,
  module?: string,
) => {
  try {
    const currentModule = getModuleName(module);
    const activeFilters = legendState
      ? getActiveMapFilters(viewOnMap, legendState)
      : {};

    analytics.track('map_filter_changed', {
      viewOnMap,
      filterType,
      filterValue,
      previousFilterValue,
      activeFilters,
      ...(viewOnMap && { dashboardTitle: viewOnMap }),
      ...(currentModule && { module: currentModule }),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackMapFilterChange', {
      function: 'trackMapFilterChange',
      viewOnMap,
      filterType,
      filterValue: JSON.stringify(filterValue),
      previousFilterValue: JSON.stringify(previousFilterValue),
    });
  }
};

/**
 * Track map click with comprehensive data
 * Updated to include active filters from PR #2329 and module tracking
 */
export const trackMapClick = (
  regionType: string,
  regionId?: string,
  regionData?: any,
  activeFilters?: Record<string, any>,
  dashboardTitle?: string,
  module?: string,
) => {
  try {
    const currentModule = getModuleName(module);
    // Use helper function to prepare analytics data based on region type
    const analyticsData = prepareMapRegionAnalyticsData(regionType, regionData);

    // Use module name as dashboardTitle if dashboardTitle is not provided
    const finalDashboardTitle = dashboardTitle || currentModule;

    // Add regionId, activeFilters, dashboardTitle, and module to the final data
    const finalAnalyticsData = {
      ...analyticsData,
      regionId,
      activeFilters: activeFilters || {},
      ...(finalDashboardTitle && { dashboardTitle: finalDashboardTitle }),
      ...(currentModule && { module: currentModule }),
    };

    analytics.track('map_click', finalAnalyticsData);
  } catch (error) {
    // Use throttled error reporting to prevent spam in Sentry
    reportAnalyticsErrorToSentry(error as Error, 'trackMapClick', {
      function: 'trackMapClick',
      regionType,
      regionId,
      dashboardTitle,
      module,
      activeFilters: JSON.stringify(activeFilters),
      regionData: JSON.stringify(regionData),
    });
  }
};

/**
 * Track REAMS property download actions
 */
/**
 * Track REAMS data download (Excel, detailed properties)
 * Note: For report downloads, use trackReamsDownloadReport() instead
 */
export const trackReamsDownload = (
  downloadType: string,
  downloadData?: any,
) => {
  try {
    analytics.track('reams_download', {
      downloadType, // 'excel', 'detailed_properties' (report downloads use trackReamsDownloadReport)
      ...downloadData,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsDownload', {
      function: 'trackReamsDownload',
      downloadType,
      downloadData: JSON.stringify(downloadData),
    });
  }
};

/**
 * Track REAMS property add action
 */
export const trackReamsAddProperty = (propertyData: any) => {
  try {
    analytics.track('reams_add_property', {
      propertyName: propertyData?.name,
      dataset: propertyData?.dataset,
      areaCode: propertyData?.areaCode,
      countryCode: propertyData?.countryCode,
      municipality: propertyData?.municipality,
      numberOfSquareMeters: propertyData?.numberOfSquareMeters,
      rentPerM2: propertyData?.rentPerM2,
      dwellingType: propertyData?.dwellingType,
      numberOfRooms: propertyData?.numberOfRooms,
      floor: propertyData?.floor,
      buildYear: propertyData?.buildYear,
      amenities: propertyData?.amenities,
      locationAnalysisType: propertyData?.locationAnalysisType,
      locationRadius: propertyData?.locationRadius,
      coordinates: propertyData?.geometry?.geometry?.coordinates,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsAddProperty', {
      function: 'trackReamsAddProperty',
      propertyData: JSON.stringify(propertyData),
    });
  }
};

/**
 * Track REAMS onboarding — fired once when the user adds their very first property
 */
export const trackReamsOnboardingFirstProperty = (propertyData: any) => {
  try {
    analytics.track('reams_onboarding_first_property', {
      propertyName: propertyData?.name,
      dataset: propertyData?.dataset,
      numberOfSquareMeters: propertyData?.numberOfSquareMeters,
      rentPerM2: propertyData?.rentPerM2,
      dwellingType: propertyData?.dwellingType,
      numberOfRooms: propertyData?.numberOfRooms,
      floor: propertyData?.floor,
      buildYear: propertyData?.buildYear,
      amenities: propertyData?.amenities,
      locationAnalysisType: propertyData?.locationAnalysisType,
      locationRadius: propertyData?.locationRadius,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(
      error as Error,
      'trackReamsOnboardingFirstProperty',
      {
        function: 'trackReamsOnboardingFirstProperty',
        propertyData: JSON.stringify(propertyData),
      },
    );
  }
};

/**
 * Track onboarding tour started — fired for any feature's onboarding tour
 * when the user confirms the intro modal. `feature` matches ONBOARDING_FEATURES.
 */
export const trackOnboardingTourStarted = (feature: string) => {
  try {
    analytics.track('onboarding_tour_started', {
      feature,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackOnboardingTourStarted', {
      function: 'trackOnboardingTourStarted',
      feature,
    });
  }
};

/**
 * Track onboarding tour skipped — fired for any feature's onboarding tour
 * when the user declines the intro modal.
 */
export const trackOnboardingTourSkipped = (feature: string) => {
  try {
    analytics.track('onboarding_tour_skipped', {
      feature,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackOnboardingTourSkipped', {
      function: 'trackOnboardingTourSkipped',
      feature,
    });
  }
};

/**
 * Track onboarding tour completed — fired for any feature's onboarding tour
 * when the user finishes the guided flow.
 */
export const trackOnboardingTourCompleted = (feature: string) => {
  try {
    analytics.track('onboarding_tour_completed', {
      feature,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(
      error as Error,
      'trackOnboardingTourCompleted',
      { function: 'trackOnboardingTourCompleted', feature },
    );
  }
};

/**
 * Track REAMS property update action
 */
export const trackReamsUpdateProperty = (
  propertyData: any,
  previousPropertyData?: any,
) => {
  try {
    analytics.track('reams_update_property', {
      propertyName: propertyData?.name,
      dataset: propertyData?.dataset,
      areaCode: propertyData?.areaCode,
      countryCode: propertyData?.countryCode,
      municipality: propertyData?.municipality,
      numberOfSquareMeters: propertyData?.numberOfSquareMeters,
      rentPerM2: propertyData?.rentPerM2,
      dwellingType: propertyData?.dwellingType,
      numberOfRooms: propertyData?.numberOfRooms,
      floor: propertyData?.floor,
      buildYear: propertyData?.buildYear,
      amenities: propertyData?.amenities,
      locationAnalysisType: propertyData?.locationAnalysisType,
      locationRadius: propertyData?.locationRadius,
      coordinates: propertyData?.geometry?.geometry?.coordinates,
      // Track what changed
      changes: previousPropertyData
        ? {
            nameChanged: propertyData?.name !== previousPropertyData?.name,
            numberOfSquareMetersChanged:
              propertyData?.numberOfSquareMeters !==
              previousPropertyData?.numberOfSquareMeters,
            rentPerM2Changed:
              propertyData?.rentPerM2 !== previousPropertyData?.rentPerM2,
            dwellingTypeChanged:
              propertyData?.dwellingType !== previousPropertyData?.dwellingType,
            numberOfRoomsChanged:
              propertyData?.numberOfRooms !==
              previousPropertyData?.numberOfRooms,
            floorChanged: propertyData?.floor !== previousPropertyData?.floor,
            buildYearChanged:
              propertyData?.buildYear !== previousPropertyData?.buildYear,
            amenitiesChanged:
              JSON.stringify(propertyData?.amenities) !==
              JSON.stringify(previousPropertyData?.amenities),
            locationAnalysisTypeChanged:
              propertyData?.locationAnalysisType !==
              previousPropertyData?.locationAnalysisType,
            locationRadiusChanged:
              propertyData?.locationRadius !==
              previousPropertyData?.locationRadius,
          }
        : undefined,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsUpdateProperty', {
      function: 'trackReamsUpdateProperty',
      propertyData: JSON.stringify(propertyData),
      previousPropertyData: JSON.stringify(previousPropertyData),
    });
  }
};

/**
 * Track REAMS property delete action
 */
export const trackReamsDeleteProperty = (propertyData: any) => {
  try {
    analytics.track('reams_delete_property', {
      propertyName: propertyData?.name,
      dataset: propertyData?.dataset,
      areaCode: propertyData?.areaCode,
      countryCode: propertyData?.countryCode,
      municipality: propertyData?.municipality,
      numberOfSquareMeters: propertyData?.numberOfSquareMeters,
      rentPerM2: propertyData?.rentPerM2,
      dwellingType: propertyData?.dwellingType,
      numberOfRooms: propertyData?.numberOfRooms,
      floor: propertyData?.floor,
      buildYear: propertyData?.buildYear,
      amenities: propertyData?.amenities,
      locationAnalysisType: propertyData?.locationAnalysisType,
      locationRadius: propertyData?.locationRadius,
      coordinates: propertyData?.geometry?.geometry?.coordinates,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsDeleteProperty', {
      function: 'trackReamsDeleteProperty',
      propertyData: JSON.stringify(propertyData),
    });
  }
};

/**
 * Track REAMS property search
 */
export const trackReamsPropertySearch = (
  searchTerm: string,
  searchResults?: any,
  filters?: any,
) => {
  try {
    analytics.track('reams_property_search', {
      searchTerm,
      searchResultsCount: searchResults?.length || 0,
      filters: filters
        ? {
            selectedDatasets: filters?.selectedDatasets,
            searchAttributes: filters?.searchAttributes,
          }
        : undefined,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsPropertySearch', {
      function: 'trackReamsPropertySearch',
      searchTerm,
      searchResultsCount: searchResults?.length || 0,
      filters: JSON.stringify(filters),
    });
  }
};

/**
 * Track REAMS property view details
 */
export const trackReamsPropertyViewDetails = (
  propertyData: any,
  viewType?: string,
) => {
  try {
    analytics.track('reams_property_view_details', {
      propertyName: propertyData?.name,
      dataset: propertyData?.dataset,
      areaCode: propertyData?.areaCode,
      countryCode: propertyData?.countryCode,
      municipality: propertyData?.municipality,
      viewType: viewType || 'general', // 'general', 'rent-estimation', 'edit'
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(
      error as Error,
      'trackReamsPropertyViewDetails',
      {
        function: 'trackReamsPropertyViewDetails',
        propertyData: JSON.stringify(propertyData),
        viewType,
      },
    );
  }
};

/**
 * Track REAMS property generate report
 */
export const trackReamsGenerateReport = (propertyData: any) => {
  try {
    analytics.track('reams_generate_report', {
      propertyName: propertyData?.name,
      dataset: propertyData?.dataset,
      areaCode: propertyData?.areaCode,
      countryCode: propertyData?.countryCode,
      municipality: propertyData?.municipality,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsGenerateReport', {
      function: 'trackReamsGenerateReport',
      propertyData: JSON.stringify(propertyData),
    });
  }
};

/**
 * Track REAMS report download (for already generated reports)
 * Separate from trackReamsGenerateReport which tracks report generation
 */
export const trackReamsDownloadReport = (propertyData: any) => {
  try {
    analytics.track('reams_download_report', {
      propertyName: propertyData?.name,
      dataset: propertyData?.dataset,
      areaCode: propertyData?.areaCode,
      countryCode: propertyData?.countryCode,
      municipality: propertyData?.municipality,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsDownloadReport', {
      function: 'trackReamsDownloadReport',
      propertyData: JSON.stringify(propertyData),
    });
  }
};

/**
 * Track REAMS SharePoint property open
 */
export const trackReamsSharepointOpen = (
  propertyData: any,
  source: 'popup' | 'details',
) => {
  try {
    analytics.track('reams_sharepoint_open', {
      propertyName: propertyData?.name,
      propertyId: propertyData?.id,
      dataset: propertyData?.dataset,
      areaCode: propertyData?.areaCode,
      countryCode: propertyData?.countryCode,
      municipality: propertyData?.municipality,
      source, // 'popup' | 'details'
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsSharepointOpen', {
      function: 'trackReamsSharepointOpen',
      propertyData: JSON.stringify(propertyData),
      source,
    });
  }
};

/**
 * Track REAMS property filter selection
 */
export const trackReamsPropertyFilter = (
  filterType: string,
  filterValue: any,
  previousFilter?: any,
) => {
  try {
    analytics.track('reams_property_filter', {
      filterType, // 'dataset', 'search', 'location_type'
      filterValue,
      previousFilter,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsPropertyFilter', {
      function: 'trackReamsPropertyFilter',
      filterType,
      filterValue: JSON.stringify(filterValue),
      previousFilter: JSON.stringify(previousFilter),
    });
  }
};

// ===== RETAIL TRACKING FUNCTIONS =====

/**
 * Track retail place addition
 */
export const trackRetailAddPlace = (placeData: any, retailFeature: string) => {
  try {
    analytics.track('retail_add_place', {
      placeId: placeData?.id,
      placeName: placeData?.name,
      placeAddress: placeData?.address,
      placeCategory: placeData?.category,
      placeAttractiveness: placeData?.attractiveness,
      placeBusinessLine: placeData?.businessLine,
      placeAttractivenessAdjustmentPercentage:
        placeData?.attractivenessAdjustmentPercentage,
      placeStoreSize: placeData?.storeSize,
      placeCoordinates: placeData?.geometry?.geometry?.coordinates,
      retailFeature, // 'catchmentAnalysis' or 'marketAnalysis'
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackRetailAddPlace', {
      function: 'trackRetailAddPlace',
      placeData: JSON.stringify(placeData),
      retailFeature,
    });
  }
};

/**
 * Track retail place update
 */
export const trackRetailUpdatePlace = (
  placeData: any,
  previousPlaceData: any,
  retailFeature: string,
) => {
  try {
    analytics.track('retail_update_place', {
      placeId: placeData?.id,
      placeName: placeData?.name,
      placeAddress: placeData?.address,
      placeCategory: placeData?.category,
      placeAttractiveness: placeData?.attractiveness,
      placeBusinessLine: placeData?.businessLine,
      placeAttractivenessAdjustmentPercentage:
        placeData?.attractivenessAdjustmentPercentage,
      placeStoreSize: placeData?.storeSize,
      placeCoordinates: placeData?.geometry?.geometry?.coordinates,
      retailFeature, // 'catchmentAnalysis' or 'marketAnalysis'
      changes: {
        nameChanged: placeData?.name !== previousPlaceData?.name,
        addressChanged: placeData?.address !== previousPlaceData?.address,
        categoryChanged: placeData?.category !== previousPlaceData?.category,
        attractivenessChanged:
          placeData?.attractiveness !== previousPlaceData?.attractiveness,
        businessLineChanged:
          placeData?.businessLine !== previousPlaceData?.businessLine,
        attractivenessAdjustmentPercentageChanged:
          placeData?.attractivenessAdjustmentPercentage !==
          previousPlaceData?.attractivenessAdjustmentPercentage,
        storeSizeChanged: placeData?.storeSize !== previousPlaceData?.storeSize,
        coordinatesChanged:
          JSON.stringify(placeData?.geometry?.geometry?.coordinates) !==
          JSON.stringify(previousPlaceData?.geometry?.geometry?.coordinates),
      },
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackRetailUpdatePlace', {
      function: 'trackRetailUpdatePlace',
      placeData: JSON.stringify(placeData),
      previousPlaceData: JSON.stringify(previousPlaceData),
      retailFeature,
    });
  }
};

/**
 * Track retail place deletion
 */
export const trackRetailDeletePlace = (
  placeData: any,
  retailFeature: string,
) => {
  try {
    analytics.track('retail_delete_place', {
      placeId: placeData?.id,
      placeName: placeData?.name,
      placeAddress: placeData?.address,
      placeCategory: placeData?.category,
      placeAttractiveness: placeData?.attractiveness,
      placeBusinessLine: placeData?.businessLine,
      placeAttractivenessAdjustmentPercentage:
        placeData?.attractivenessAdjustmentPercentage,
      placeStoreSize: placeData?.storeSize,
      placeCoordinates: placeData?.geometry?.geometry?.coordinates,
      retailFeature, // 'catchmentAnalysis' or 'marketAnalysis'
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackRetailDeletePlace', {
      function: 'trackRetailDeletePlace',
      placeData: JSON.stringify(placeData),
      retailFeature,
    });
  }
};

/**
 * Track retail data download
 */
export const trackRetailDownload = (
  downloadType: string,
  downloadData?: any,
  retailFeature?: string,
) => {
  try {
    analytics.track('retail_download', {
      downloadType, // 'csv', 'report', etc.
      retailFeature, // 'catchmentAnalysis' or 'marketAnalysis'
      placesCount: downloadData?.placesCount || 0,
      fileName: downloadData?.fileName,
      businessLine: downloadData?.businessLine,
      catchmentMethod: downloadData?.catchmentMethod,
      reachabilityMode: downloadData?.reachabilityMode,
      reachabilityDuration: downloadData?.reachabilityDuration,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackRetailDownload', {
      function: 'trackRetailDownload',
      downloadType,
      downloadData: JSON.stringify(downloadData),
      retailFeature,
    });
  }
};

/**
 * Track screen resize events
 */
export const trackScreenResize = (
  previousScreenInfo?: any,
  module?: string,
) => {
  try {
    const screenInfo = getScreenInfo();
    const currentModule = getModuleName(module);

    // Only track if there's a meaningful change (device type or orientation changed)
    // This prevents tracking every pixel change during window resize
    const hasDeviceTypeChanged =
      previousScreenInfo?.deviceType !== screenInfo.deviceType;
    const hasOrientationChanged =
      previousScreenInfo?.orientation !== screenInfo.orientation;

    if (hasDeviceTypeChanged || hasOrientationChanged || !previousScreenInfo) {
      analytics.track('screen_resize', {
        // Previous state
        previousDeviceType: previousScreenInfo?.deviceType,
        previousOrientation: previousScreenInfo?.orientation,
        previousViewportWidth: previousScreenInfo?.windowWidth,
        previousViewportHeight: previousScreenInfo?.windowHeight,

        // New state
        deviceType: screenInfo.deviceType,
        orientation: screenInfo.orientation,
        viewport: {
          width: screenInfo.windowWidth,
          height: screenInfo.windowHeight,
        },
        screen: {
          width: screenInfo.screenWidth,
          height: screenInfo.screenHeight,
        },
        devicePixelRatio: screenInfo.devicePixelRatio,
        touchSupport: screenInfo.touchSupport,
        ...(currentModule && { module: currentModule }),
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackScreenResize', {
      function: 'trackScreenResize',
      module,
      previousScreenInfo: JSON.stringify(previousScreenInfo),
    });
  }
};

// ===== AREA SELECTION TRACKING =====

/**
 * Track area selection confirmed (user clicks "View new area" or selects from search bar)
 * @param mode - selection mode: 'search_bar', 'polygon', 'reachability', etc.
 * @param areaDescription - optional description when selecting from search bar
 */
export const trackAreaSelectionConfirmed = (
  mode: string,
  areaDescription?: string,
) => {
  try {
    analytics.track('area_selection_confirmed', {
      mode,
      ...(areaDescription && { areaDescription }),
      module: getCurrentModule(),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(
      error as Error,
      'trackAreaSelectionConfirmed',
      { function: 'trackAreaSelectionConfirmed', mode, areaDescription },
    );
  }
};

// ===== LOCATION OF INTEREST TRACKING =====

/**
 * Track location of interest actions
 */
export const trackLocationOfInterest = (
  action: string,
  data?: Record<string, any>,
) => {
  try {
    analytics.track('location_of_interest_action', {
      action,
      ...data,
      module: getCurrentModule(),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackLocationOfInterest', {
      function: 'trackLocationOfInterest',
      action,
      data: JSON.stringify(data),
    });
  }
};

/**
 * Rent Navigator building grouping events. Each is sent as its own
 * `reams_building_<event>` event so it can be charted on its own in Amplitude.
 */
export type ReamsBuildingEvent =
  | 'view_mode_changed' // properties <-> buildings list
  | 'viewed' // building details opened
  | 'tab_viewed'
  | 'created'
  | 'updated'
  | 'deleted'
  | 'units_saved' // units added, edited or removed in the units editor
  | 'units_imported' // rows taken over from a spreadsheet
  | 'units_moved'
  | 'add_rents_clicked'
  | 'scenario_created'
  | 'scenario_deleted';

type IReamsBuildingLike = {
  buildingId?: number | null;
  dataset?: string | null;
  countryCode?: string | null;
  municipality?: string | null;
  city?: string | null;
  areaCode?: string | null;
  postalCode?: string | null;
  unitsCount?: number | null;
};

/** The building properties sent with every building event (no address or name). */
export const toReamsBuildingAnalytics = (
  building?: IReamsBuildingLike | null,
) =>
  building
    ? {
        buildingId: building.buildingId ?? undefined,
        dataset: building.dataset ?? undefined,
        countryCode: building.countryCode ?? undefined,
        municipality: building.municipality ?? building.city ?? undefined,
        areaCode: building.areaCode ?? building.postalCode ?? undefined,
        unitsCount: building.unitsCount ?? undefined,
      }
    : {};

export const trackReamsBuilding = (
  event: ReamsBuildingEvent,
  data?: Record<string, any>,
) => {
  try {
    analytics.track(`reams_building_${event}`, {
      ...data,
      module: getCurrentModule() || 'Optimize',
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    reportAnalyticsErrorToSentry(error as Error, 'trackReamsBuilding', {
      function: 'trackReamsBuilding',
      event,
      data: JSON.stringify(data),
    });
  }
};
